"use client";

import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { API_URL, REGISTRATION_OPEN } from "./config";
import {
  Box,
  Typography,
  Button,
  Container,
  Grid,
  Card,
  CardContent,
  IconButton,
  Fade,
  Zoom,
  Slide,
  Chip,
  TextField,
  Snackbar,
  Alert,
} from "@mui/material";
import { styled, keyframes } from "@mui/system";
import { useNavigate } from "react-router-dom";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import LoginIcon from "@mui/icons-material/Login";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import QuizIcon from "@mui/icons-material/Quiz";
import GroupsIcon from "@mui/icons-material/Groups";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import SpeedIcon from "@mui/icons-material/Speed";
import MusicNoteIcon from "@mui/icons-material/MusicNote";
import LeaderboardIcon from "@mui/icons-material/Leaderboard";
import TouchAppIcon from "@mui/icons-material/TouchApp";
import SportsEsportsIcon from "@mui/icons-material/SportsEsports";
import SettingsIcon from "@mui/icons-material/Settings";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import InfoIcon from "@mui/icons-material/Info";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import BoltIcon from "@mui/icons-material/Bolt";
import FavoriteIcon from "@mui/icons-material/Favorite";
import WhatshotIcon from "@mui/icons-material/Whatshot";
import CelebrationIcon from "@mui/icons-material/Celebration";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import StarIcon from "@mui/icons-material/Star";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import SendIcon from "@mui/icons-material/Send";
import SecurityIcon from "@mui/icons-material/Security";
import DevicesIcon from "@mui/icons-material/Devices";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import SchoolIcon from "@mui/icons-material/School";
import BusinessIcon from "@mui/icons-material/Business";
import FamilyRestroomIcon from "@mui/icons-material/FamilyRestroom";
import CompareIcon from "@mui/icons-material/Compare";
import CloseIcon from "@mui/icons-material/Close";
import DoneIcon from "@mui/icons-material/Done";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";

/* ═══════════════════════════════════════════════════════
   ✨ KEYFRAMES
   ═══════════════════════════════════════════════════════ */

const gradientShift = keyframes`
  0%   { background-position: 0% 50%; }
  50%  { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

const float = keyframes`
  0%, 100% { transform: translateY(0); }
  50%      { transform: translateY(-16px); }
`;

const floatSlow = keyframes`
  0%, 100% { transform: translateY(0) rotate(0deg); }
  25%      { transform: translateY(-10px) rotate(3deg); }
  75%      { transform: translateY(6px) rotate(-2deg); }
`;

const shimmer = keyframes`
  0%   { background-position: -200% center; }
  100% { background-position: 200% center; }
`;

const pulseGlow = keyframes`
  0%   { box-shadow: 0 0 0 0 rgba(79,70,229,.45); }
  70%  { box-shadow: 0 0 0 16px rgba(79,70,229,0); }
  100% { box-shadow: 0 0 0 0 rgba(79,70,229,0); }
`;

const scrollBounce = keyframes`
  0%, 100% { transform: translateY(0); opacity: 1; }
  50%      { transform: translateY(10px); opacity: .4; }
`;

const blobAnim1 = keyframes`
  0%, 100% { transform: translate(0,0) scale(1); }
  33%      { transform: translate(25px,-35px) scale(1.05); }
  66%      { transform: translate(-15px,20px) scale(.97); }
`;

const blobAnim2 = keyframes`
  0%, 100% { transform: translate(0,0) scale(1); }
  33%      { transform: translate(-30px,25px) scale(.95); }
  66%      { transform: translate(20px,-15px) scale(1.06); }
`;

const marquee = keyframes`
  0%   { transform: translateX(0); }
  100% { transform: translateX(-50%); }
`;

const ripple = keyframes`
  0%   { transform: scale(1); opacity: .4; }
  100% { transform: scale(2.4); opacity: 0; }
`;

const typewriter = keyframes`
  from { width: 0; }
  to   { width: 100%; }
`;

/* ═══════════════════════════════════════════════════════
   🎨 COLORS — bright, clean, modern
   ═══════════════════════════════════════════════════════ */

// כשההרשמה סגורה הכפתורים עדיין מובילים למסך ההרשמה, שמציג את פרטי יצירת הקשר
const CTA_LABEL = REGISTRATION_OPEN ? "התחילו עכשיו - בחינם!" : "לפתיחת חשבון - צרו קשר";
const LOGIN_LABEL = REGISTRATION_OPEN ? "כניסה / הרשמה" : "כניסה";

const C = {
  primary: "#4f46e5", primaryL: "#6366f1", primaryXL: "#eef2ff",
  secondary: "#7c3aed",
  amber: "#f59e0b", emerald: "#10b981", cyan: "#06b6d4",
  pink: "#ec4899", rose: "#f43f5e",
  white: "#ffffff",
  bg: "#f8fafc", bgSoft: "#eef2ff",
  textDark: "#1e293b", textMid: "#475569", textLight: "#94a3b8",
  border: "#e2e8f0",
};

/* ═══════════════════════════════════════════════════════
   🪝 HOOKS
   ═══════════════════════════════════════════════════════ */

/** Parallax offset */
const useParallax = (speed = .3) => {
  const [offset, setOffset] = useState(0);
  const ref = useRef(null);
  useEffect(() => {
    const h = () => {
      if (!ref.current) return;
      const rect = ref.current.getBoundingClientRect();
      const viewH = window.innerHeight;
      if (rect.top < viewH && rect.bottom > 0) {
        setOffset((viewH - rect.top) * speed);
      }
    };
    window.addEventListener("scroll", h, { passive: true });
    h();
    return () => window.removeEventListener("scroll", h);
  }, [speed]);
  return { ref, offset };
};

/** Visible on scroll */
const useScrollVis = (threshold = .08) => {
  const [vis, setVis] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setVis(true); }, { threshold });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [threshold]);
  return { ref, vis };
};

/** Counter animation */
const useCountUp = (end, duration = 2000, start = 0, active = false) => {
  const [value, setValue] = useState(start);
  useEffect(() => {
    if (!active) return;
    let startTime = null;
    const numEnd = parseInt(String(end).replace(/[^0-9]/g, "")) || 0;
    const step = (ts) => {
      if (!startTime) startTime = ts;
      const progress = Math.min((ts - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setValue(Math.floor(eased * numEnd));
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [end, duration, start, active]);
  return value;
};

/* ═══════════════════════════════════════════════════════
   🧩 SHARED
   ═══════════════════════════════════════════════════════ */

const HeroBlobs = () => (
  <Box sx={{ position: "absolute", inset: 0, overflow: "hidden", pointerEvents: "none", zIndex: 0 }}>
    <Box sx={{ position: "absolute", width: { xs: 250, md: 420 }, height: { xs: 250, md: 420 }, borderRadius: "50%", filter: "blur(90px)", opacity: .5, background: "radial-gradient(circle, #c7d2fe, transparent)", top: "-8%", right: "-4%", animation: `${blobAnim1} 18s ease-in-out infinite` }} />
    <Box sx={{ position: "absolute", width: { xs: 200, md: 380 }, height: { xs: 200, md: 380 }, borderRadius: "50%", filter: "blur(90px)", opacity: .45, background: "radial-gradient(circle, #ddd6fe, transparent)", bottom: "0%", left: "-6%", animation: `${blobAnim2} 22s ease-in-out infinite` }} />
    <Box sx={{ position: "absolute", width: { xs: 180, md: 300 }, height: { xs: 180, md: 300 }, borderRadius: "50%", filter: "blur(80px)", opacity: .35, background: "radial-gradient(circle, #bae6fd, transparent)", top: "55%", left: "60%", animation: `${blobAnim1} 20s 4s ease-in-out infinite` }} />
    <Box sx={{ position: "absolute", width: 250, height: 250, borderRadius: "50%", filter: "blur(80px)", opacity: .3, background: "radial-gradient(circle, #fde68a, transparent)", top: "25%", left: "15%", animation: `${blobAnim2} 16s 2s ease-in-out infinite` }} />
  </Box>
);

const FloatingEmojis = () => {
  const items = useMemo(() => [
    { e: "🎮", x: 4, y: 18, s: 30, d: 7, dl: 0 }, { e: "🏆", x: 90, y: 15, s: 26, d: 8, dl: 1 },
    { e: "⚡", x: 10, y: 75, s: 22, d: 6, dl: 2.5 }, { e: "🎯", x: 93, y: 68, s: 28, d: 9, dl: .5 },
    { e: "✨", x: 50, y: 6, s: 24, d: 7, dl: 3 }, { e: "🚀", x: 82, y: 8, s: 28, d: 6, dl: 4 },
    { e: "💎", x: 18, y: 88, s: 20, d: 8, dl: 1.5 }, { e: "🎵", x: 72, y: 82, s: 22, d: 7, dl: 2 },
    { e: "🎪", x: 35, y: 92, s: 24, d: 6, dl: 3.5 }, { e: "🌟", x: 60, y: 3, s: 20, d: 9, dl: 1 },
  ], []);
  return (
    <Box sx={{ position: "absolute", inset: 0, overflow: "hidden", pointerEvents: "none", zIndex: 1 }}>
      {items.map((it, i) => (
        <Box key={i} sx={{
          position: "absolute", left: `${it.x}%`, top: `${it.y}%`, fontSize: it.s,
          animation: `${floatSlow} ${it.d}s ${it.dl}s ease-in-out infinite`,
          opacity: { xs: .25, md: .4 },
          display: { xs: i > 5 ? "none" : "block", md: "block" },
        }}>{it.e}</Box>
      ))}
    </Box>
  );
};

const ModernCard = styled(Card)(({ accent }) => ({
  background: "#fff",
  borderRadius: 20,
  border: "1px solid #e2e8f0",
  boxShadow: "0 4px 24px rgba(0,0,0,.06)",
  transition: "all .35s cubic-bezier(.25,.8,.25,1)",
  overflow: "visible",
  position: "relative",
  "&:hover": {
    transform: "translateY(-8px)",
    boxShadow: `0 16px 48px ${accent ? accent + "22" : "rgba(79,70,229,.12)"}`,
    borderColor: accent || C.primaryL,
  },
}));

const SectionDivider = ({ flip, color1 = C.primaryXL, color2 = C.white }) => (
  <Box sx={{ width: "100%", overflow: "hidden", lineHeight: 0, transform: flip ? "rotate(180deg)" : "none", mt: flip ? 0 : "-1px", mb: flip ? "-1px" : 0 }}>
    <svg viewBox="0 0 1200 120" preserveAspectRatio="none" style={{ width: "100%", height: 60 }}>
      <path d="M0,0V46.29c47.79,22.2,103.59,32.17,158,28,70.36-5.37,136.33-33.31,206.8-37.5C438.64,32.43,512.34,53.67,583,72.05c69.27,18,138.3,24.88,209.4,13.08,36.15-6,69.85-17.84,104.45-29.34C989.49,25,1113-14.29,1200,52.47V0Z" fill={color1} opacity=".25" />
      <path d="M0,0V15.81C13,36.92,27.64,56.86,47.69,72.05,99.41,111.27,165,111,224.58,91.58c31.15-10.15,60.09-26.07,89.67-39.8,40.92-19,84.73-46,130.83-49.67,36.26-2.85,70.9,9.42,98.6,31.56,31.77,25.39,62.32,62,103.63,73,40.44,10.79,81.35-6.69,119.13-24.28s75.16-39,116.92-43.05c59.73-5.85,113.28,22.88,168.9,38.84,30.2,8.66,59,6.17,87.09-7.5,22.43-10.89,48-26.93,60.65-49.24V0Z" fill={color1} opacity=".5" />
      <path d="M0,0V5.63C149.93,59,314.09,71.32,475.83,42.57c43-7.64,84.23-20.12,127.61-26.46,59-8.63,112.48,12.24,165.56,35.4C827.93,77.22,886,95.24,951.2,90c86.53-7,172.46-45.71,248.8-84.81V0Z" fill={color1} />
    </svg>
  </Box>
);

/* ═══════════════════════════════════════════════════════
   🧭 NAVBAR — with scroll-to-section links
   ═══════════════════════════════════════════════════════ */

const navLinks = [
  { label: "תכונות", id: "features" },
  { label: "איך זה עובד", id: "how" },
  { label: "מי משתמש", id: "usecases" },
  { label: "צור קשר", id: "contact" },
];

const Navbar = () => {
  const nav = useNavigate();
  const [scrolled, setScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState("");

  useEffect(() => {
    const h = () => {
      setScrolled(window.scrollY > 50);
      // highlight active section
      for (const link of [...navLinks].reverse()) {
        const el = document.getElementById(link.id);
        if (el && el.getBoundingClientRect().top < 200) {
          setActiveSection(link.id);
          return;
        }
      }
      setActiveSection("");
    };
    window.addEventListener("scroll", h, { passive: true });
    return () => window.removeEventListener("scroll", h);
  }, []);

  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });

  return (
    <Box sx={{
      position: "fixed", top: 0, left: 0, right: 0, zIndex: 50,
      py: 1.2, px: { xs: 2, md: 4 },
      display: "flex", alignItems: "center", justifyContent: "space-between",
      background: scrolled ? "rgba(255,255,255,.92)" : "transparent",
      backdropFilter: scrolled ? "blur(16px)" : "none",
      borderBottom: scrolled ? `1px solid ${C.border}` : "none",
      transition: "all .3s",
    }}>
      {/* logo */}
      <Box onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
        sx={{ display: "flex", alignItems: "center", gap: 1.2, cursor: "pointer", userSelect: "none" }}>
        <Box sx={{ width: 40, height: 40, borderRadius: "14px", background: `linear-gradient(135deg, ${C.primary}, ${C.secondary})`, display: "flex", alignItems: "center", justifyContent: "center", boxShadow: `0 4px 12px ${C.primary}33` }}>
          <SportsEsportsIcon sx={{ color: "#fff", fontSize: 24 }} />
        </Box>
        <Typography sx={{ fontWeight: 900, fontSize: "1.5rem", color: C.primary, fontFamily: "'Varela Round', 'Rubik', sans-serif" }}>קליקרים</Typography>
      </Box>

      {/* nav links */}
      <Box sx={{ display: "flex", gap: { xs: .3, sm: 1 }, alignItems: "center" }}>
        {navLinks.map((l) => (
          <Button key={l.id} onClick={() => scrollTo(l.id)} sx={{
            color: activeSection === l.id ? C.primary : C.textMid,
            textTransform: "none", fontWeight: activeSection === l.id ? 800 : 600,
            borderRadius: 3, fontSize: { xs: ".75rem", sm: ".88rem" },
            position: "relative",
            "&:hover": { color: C.primary, background: C.primaryXL },
            display: { xs: "none", md: "inline-flex" },
            "&::after": activeSection === l.id ? {
              content: '""', position: "absolute", bottom: 2, left: "20%", right: "20%", height: 3,
              borderRadius: 2, background: `linear-gradient(90deg, ${C.primary}, ${C.secondary})`,
            } : {},
          }}>{l.label}</Button>
        ))}
        <Button onClick={() => nav("/about")} sx={{
          color: C.textMid, textTransform: "none", fontWeight: 600, borderRadius: 3,
          "&:hover": { color: C.primary, background: C.primaryXL },
          display: { xs: "none", sm: "inline-flex" }, fontSize: { xs: ".75rem", sm: ".88rem" },
        }}>אודות</Button>
        <Button onClick={() => nav("/login")} sx={{
          color: "#fff", textTransform: "none", fontWeight: 700, borderRadius: 50,
          px: { xs: 2, sm: 3 }, fontSize: { xs: ".8rem", sm: ".88rem" },
          background: `linear-gradient(135deg, ${C.primary}, ${C.secondary})`,
          boxShadow: `0 4px 14px ${C.primary}44`,
          "&:hover": { boxShadow: `0 6px 22px ${C.primary}55`, transform: "scale(1.03)" },
          transition: "all .3s",
        }}>
          <LoginIcon sx={{ ml: .8, fontSize: 18 }} /> {LOGIN_LABEL}
        </Button>
      </Box>
    </Box>
  );
};

/* ═══════════════════════════════════════════════════════
   🚀 HERO — with parallax
   ═══════════════════════════════════════════════════════ */

const HeroSection = () => {
  const nav = useNavigate();
  const [loaded, setLoaded] = useState(false);
  const { ref: pRef, offset } = useParallax(.15);
  useEffect(() => setLoaded(true), []);

  return (
    <Box ref={pRef} sx={{
      minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center",
      position: "relative", overflow: "hidden",
      background: `linear-gradient(170deg, #ffffff 0%, ${C.bgSoft} 40%, #f0f4ff 70%, #faf5ff 100%)`,
    }}>
      <HeroBlobs />

      <Fade in={loaded} timeout={1000}>
        <Box sx={{ textAlign: "center", zIndex: 5, px: 2, maxWidth: 820, mx: "auto", pt: 8, transform: `translateY(${-offset * .15}px)` }}>
          <Zoom in={loaded} timeout={500}>
            <Chip icon={<WhatshotIcon sx={{ color: `${C.amber} !important`, fontSize: 18 }} />}
              label="הפלטפורמה #1 לחידונים טלפוניים אינטראקטיביים"
              sx={{ mb: 3, py: 2.5, px: 1, background: "#fff", border: `1px solid ${C.border}`, boxShadow: "0 2px 8px rgba(0,0,0,.06)", color: C.textDark, fontWeight: 600, fontSize: ".88rem", "& .MuiChip-icon": { mr: 0 } }} />
          </Zoom>

          <Zoom in={loaded} timeout={700}>
            <Box sx={{
              width: { xs: 80, md: 100 }, height: { xs: 80, md: 100 }, borderRadius: "28px", mx: "auto", mb: 3,
              background: `linear-gradient(135deg, ${C.primary}, ${C.secondary}, ${C.cyan})`,
              backgroundSize: "200% 200%",
              animation: `${gradientShift} 5s ease infinite, ${float} 5s ease-in-out infinite`,
              display: "flex", alignItems: "center", justifyContent: "center",
              boxShadow: `0 12px 40px ${C.primary}33`,
              position: "relative",
            }}>
              <SportsEsportsIcon sx={{ fontSize: { xs: 44, md: 56 }, color: "#fff" }} />
              {/* ripple rings */}
              {[0, .6, 1.2].map((d, i) => (
                <Box key={i} sx={{
                  position: "absolute", inset: -4, borderRadius: "28px",
                  border: `2px solid ${C.primary}44`,
                  animation: `${ripple} 3s ${d}s ease-out infinite`,
                }} />
              ))}
            </Box>
          </Zoom>

          <Typography variant="h1" sx={{
            fontSize: { xs: "3rem", sm: "4.5rem", md: "5.5rem" },
            fontWeight: 900, lineHeight: 1.1, mb: 2.5, color: C.textDark,
            fontFamily: "'Varela Round', 'Rubik', sans-serif",
          }}>
            ברוכים הבאים ל
            <Box component="span" sx={{
              background: `linear-gradient(135deg, ${C.primary}, ${C.secondary}, ${C.cyan})`,
              backgroundSize: "200% auto", backgroundClip: "text",
              WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent",
              animation: `${shimmer} 4s linear infinite`,
            }}>קליקרים</Box>
          </Typography>

          <Typography sx={{
            fontSize: { xs: "1.1rem", sm: "1.3rem", md: "1.45rem" },
            color: C.textMid, fontWeight: 400, lineHeight: 1.9, mb: 2, maxWidth: 600, mx: "auto",
          }}>
            צרו משחקי חידון מדהימים — השחקנים מתקשרים למספר ועונים{" "}
            <Box component="span" sx={{ color: C.primary, fontWeight: 700 }}>ישירות מהטלפון</Box>{" "}
            בלחיצת כפתור. חוויה אינטראקטיבית בזמן אמת!
          </Typography>

          <Box sx={{ display: "flex", gap: 1.5, justifyContent: "center", flexWrap: "wrap", mb: 4 }}>
            {[{ label: "חידון טלפוני", icon: "📞" }, { label: "בזמן אמת", icon: "⚡" }, { label: "תחרויות", icon: "🏆" }, { label: "מדיה", icon: "🎵" }].map((t, i) => (
              <Chip key={i} label={`${t.icon} ${t.label}`} sx={{ background: "#fff", border: `1px solid ${C.border}`, color: C.textMid, fontWeight: 600, fontSize: ".85rem", boxShadow: "0 1px 4px rgba(0,0,0,.04)" }} />
            ))}
          </Box>

          <Box sx={{ display: "flex", gap: 2, justifyContent: "center", flexWrap: "wrap" }}>
            <Button onClick={() => nav("/login?tab=register")} sx={{
              background: `linear-gradient(135deg, ${C.primary}, ${C.secondary})`,
              backgroundSize: "200% 200%",
              animation: `${gradientShift} 4s ease infinite, ${pulseGlow} 3s infinite`,
              color: "#fff", fontWeight: 800, fontSize: { xs: "1rem", sm: "1.12rem" },
              borderRadius: 50, px: { xs: 4, sm: 5 }, py: 1.8, textTransform: "none",
              boxShadow: `0 8px 28px ${C.primary}44`,
              "&:hover": { transform: "scale(1.04)", boxShadow: `0 12px 36px ${C.primary}55` },
              transition: "all .3s",
            }}>
              <RocketLaunchIcon sx={{ ml: 1 }} /> {CTA_LABEL}
            </Button>
            <Button onClick={() => nav("/about")} sx={{
              color: C.primary, fontWeight: 700, fontSize: "1rem",
              borderRadius: 50, px: 4, py: 1.5, border: `2px solid ${C.primary}44`,
              textTransform: "none", background: "#fff",
              "&:hover": { background: C.primaryXL, borderColor: C.primary },
            }}>
              <InfoIcon sx={{ ml: 1, fontSize: 20 }} /> מה זה קליקרים?
            </Button>
          </Box>

          <Box onClick={() => document.getElementById("features")?.scrollIntoView({ behavior: "smooth" })}
            sx={{ mt: 6, cursor: "pointer", color: C.textLight, animation: `${scrollBounce} 2s ease-in-out infinite` }}>
            <ArrowDownwardIcon sx={{ fontSize: 32 }} />
          </Box>
        </Box>
      </Fade>
    </Box>
  );
};

/* ═══════════════════════════════════════════════════════
   🏢 MARQUEE — trust bar
   ═══════════════════════════════════════════════════════ */

const marqueeItems = [
  "🏫 בתי ספר", "🎓 מכללות", "🏢 חברות הייטק", "🎉 חברות אירועים",
  "👨‍👩‍👧‍👦 משפחות", "🎤 מנחים", "📚 מורים", "⛺ קייטנות",
  "🏫 בתי ספר", "🎓 מכללות", "🏢 חברות הייטק", "🎉 חברות אירועים",
  "👨‍👩‍👧‍👦 משפחות", "🎤 מנחים", "📚 מורים", "⛺ קייטנות",
];

const MarqueeBanner = () => (
  <Box sx={{ py: 3, background: C.primaryXL, borderTop: `1px solid ${C.border}`, borderBottom: `1px solid ${C.border}`, overflow: "hidden", position: "relative" }}>
    <Box sx={{ display: "flex", width: "max-content", animation: `${marquee} 30s linear infinite` }}>
      {marqueeItems.map((item, i) => (
        <Typography key={i} sx={{ mx: 4, fontSize: "1rem", fontWeight: 600, color: C.textMid, whiteSpace: "nowrap", userSelect: "none" }}>{item}</Typography>
      ))}
    </Box>
  </Box>
);

/* ═══════════════════════════════════════════════════════
   ⭐ FEATURES — with parallax cards
   ═══════════════════════════════════════════════════════ */

const features = [
  { icon: <QuizIcon sx={{ fontSize: 32 }} />, title: "חידונים מותאמים", desc: "צרו שאלות בכל נושא עם תשובות, ניקוד וטיימר מותאם", color: C.primary, bg: "#eef2ff", emoji: "🎯" },
  { icon: <GroupsIcon sx={{ fontSize: 32 }} />, title: "משחק קבוצתי", desc: "חלקו לקבוצות, עקבו אחרי הניקוד ותנו לתחרות לרוץ", color: C.cyan, bg: "#ecfeff", emoji: "👥" },
  { icon: <EmojiEventsIcon sx={{ fontSize: 32 }} />, title: "לידרבורד חי", desc: "טבלת מובילים בזמן אמת עם אנימציות ניצחון מרהיבות", color: C.amber, bg: "#fffbeb", emoji: "🏆" },
  { icon: <PhoneIcon sx={{ fontSize: 32 }} />, title: "משחק טלפוני", desc: "השחקנים מתקשרים למספר ועונים בלחיצת כפתור — בלי אפליקציה ובלי אינטרנט", color: C.emerald, bg: "#ecfdf5", emoji: "📞" },
  { icon: <SettingsIcon sx={{ fontSize: 32 }} />, title: "פאנל ניהול חזק", desc: "נהלו משחקים, שאלות, שחקנים וקבוצות ממקום אחד", color: "#7c3aed", bg: "#f5f3ff", emoji: "⚙️" },
  { icon: <MusicNoteIcon sx={{ fontSize: 32 }} />, title: "מדיה ואפקטים", desc: "העלו מוזיקה, תמונות, סרטונים ותנו למשחק לחיות", color: C.pink, bg: "#fdf2f8", emoji: "🎵" },
];

const FeaturesSection = () => {
  const { ref, vis } = useScrollVis();
  const { ref: pRef, offset } = useParallax(.06);

  return (
    <Box id="features" ref={(el) => { ref.current = el; pRef.current = el; }} sx={{ py: { xs: 8, md: 12 }, px: 2, background: C.white, position: "relative" }}>
      <Container maxWidth="lg">
        <Fade in={vis} timeout={700}>
          <Box sx={{ textAlign: "center", mb: 7, transform: `translateY(${-offset * .2}px)` }}>
            <Chip label="תכונות" sx={{ mb: 2, background: C.primaryXL, color: C.primary, fontWeight: 700, border: `1px solid ${C.primary}22` }} />
            <Typography variant="h3" sx={{ fontWeight: 900, color: C.textDark, mb: 1.5, fontSize: { xs: "2rem", md: "2.8rem" } }}>
              למה לבחור ב<Box component="span" sx={{ color: C.primary }}>קליקרים</Box>?
            </Typography>
            <Typography sx={{ color: C.textMid, fontSize: "1.1rem", maxWidth: 500, mx: "auto" }}>
              כל הכלים שצריך כדי ליצור חוויית משחק מושלמת
            </Typography>
          </Box>
        </Fade>

        <Grid container spacing={3}>
          {features.map((f, i) => (
            <Grid item xs={12} sm={6} md={4} key={i}>
              <Zoom in={vis} timeout={400 + i * 120}>
                <ModernCard accent={f.color}>
                  <CardContent sx={{ p: 3.5, textAlign: "center" }}>
                    <Box sx={{ position: "absolute", top: -14, right: 18, width: 36, height: 36, borderRadius: "10px", background: f.bg, border: `1px solid ${f.color}22`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, boxShadow: `0 2px 8px ${f.color}15` }}>{f.emoji}</Box>
                    <Box sx={{ width: 70, height: 70, borderRadius: "20px", mx: "auto", mb: 2.5, background: f.bg, display: "flex", alignItems: "center", justifyContent: "center", color: f.color, transition: "all .3s", "&:hover": { transform: "rotate(5deg) scale(1.08)" } }}>{f.icon}</Box>
                    <Typography variant="h6" sx={{ fontWeight: 800, color: C.textDark, mb: 1, fontSize: "1.05rem" }}>{f.title}</Typography>
                    <Typography sx={{ color: C.textMid, lineHeight: 1.8, fontSize: ".9rem" }}>{f.desc}</Typography>
                  </CardContent>
                </ModernCard>
              </Zoom>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};

/* ═══════════════════════════════════════════════════════
   📋 HOW IT WORKS
   ═══════════════════════════════════════════════════════ */

const steps = [
  { title: "נרשמים בחינם", desc: "יוצרים חשבון תוך 30 שניות — בלי תשלום", color: C.primary, emoji: "📝", bg: "#eef2ff" },
  { title: "בונים משחק", desc: "שאלות, תשובות, מדיה ועוד — בחינם!", color: C.pink, emoji: "🎮", bg: "#fdf2f8" },
  { title: "השחקנים מתקשרים", desc: "מספר טלפון ייעודי — השחקנים מחייגים ועונים בלחיצת כפתור", color: C.emerald, emoji: "📞", bg: "#ecfdf5" },
  { title: "נהנים ומנצחים!", desc: "תחרות חיה עם לוח מובילים", color: C.amber, emoji: "🎉", bg: "#fffbeb" },
];

const HowItWorksSection = () => {
  const { ref, vis } = useScrollVis();
  const { ref: pRef, offset } = useParallax(.05);

  return (
    <Box id="how" ref={(el) => { ref.current = el; pRef.current = el; }} sx={{ py: { xs: 8, md: 12 }, px: 2, background: `linear-gradient(180deg, ${C.bg} 0%, ${C.bgSoft} 50%, ${C.bg} 100%)`, position: "relative" }}>
      <Container maxWidth="lg">
        <Fade in={vis} timeout={700}>
          <Box sx={{ textAlign: "center", mb: 7, transform: `translateY(${-offset * .15}px)` }}>
            <Chip label="איך זה עובד" sx={{ mb: 2, background: "#ecfdf5", color: C.emerald, fontWeight: 700, border: `1px solid ${C.emerald}22` }} />
            <Typography variant="h3" sx={{ fontWeight: 900, color: C.textDark, mb: 1.5, fontSize: { xs: "2rem", md: "2.8rem" } }}>
              ארבעה צעדים פשוטים <Box component="span" sx={{ color: C.emerald }}>ואתם בפנים</Box>
            </Typography>
          </Box>
        </Fade>

        <Grid container spacing={4}>
          {steps.map((s, i) => (
            <Grid item xs={12} sm={6} md={3} key={i}>
              <Zoom in={vis} timeout={400 + i * 180}>
                <Box sx={{ textAlign: "center", position: "relative" }}>
                  {i < steps.length - 1 && <Box sx={{ display: { xs: "none", md: "block" }, position: "absolute", top: 55, left: "-50%", width: "100%", height: 3, background: `linear-gradient(90deg, ${s.color}33, ${steps[i + 1].color}33)`, borderRadius: 2, zIndex: 0 }} />}
                  <Box sx={{
                    width: 110, height: 110, borderRadius: "50%", mx: "auto", mb: 2.5,
                    background: s.bg, border: `3px solid ${s.color}22`,
                    display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
                    position: "relative", zIndex: 1, transition: "all .35s",
                    "&:hover": { transform: "scale(1.08)", boxShadow: `0 8px 30px ${s.color}22`, borderColor: s.color },
                  }}>
                    <Typography sx={{ fontSize: "2.5rem" }}>{s.emoji}</Typography>
                    <Box sx={{ position: "absolute", top: -6, right: -6, width: 30, height: 30, borderRadius: "50%", background: `linear-gradient(135deg, ${s.color}, ${s.color}cc)`, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontWeight: 900, fontSize: ".82rem", boxShadow: `0 3px 8px ${s.color}44` }}>{i + 1}</Box>
                  </Box>
                  <Typography variant="h6" sx={{ fontWeight: 800, color: C.textDark, mb: .5, fontSize: "1.05rem" }}>{s.title}</Typography>
                  <Typography sx={{ color: C.textMid, fontSize: ".9rem" }}>{s.desc}</Typography>
                </Box>
              </Zoom>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};

/* ═══════════════════════════════════════════════════════
   🖼️ DEMO — with parallax
   ═══════════════════════════════════════════════════════ */

const screens = [
  { title: "פאנל ניהול", desc: "ממשק ניהול חזק ונוח", emoji: "⚙️", gradient: `linear-gradient(135deg, ${C.primary}, #818cf8)` },
  { title: "מסך משחק", desc: "חוויית משחק אינטראקטיבית", emoji: "🎮", gradient: `linear-gradient(135deg, ${C.emerald}, #34d399)` },
  { title: "לוח תוצאות", desc: "לידרבורד חי ומרהיב", emoji: "🏆", gradient: `linear-gradient(135deg, ${C.amber}, #fbbf24)` },
];

const DemoSection = () => {
  const { ref, vis } = useScrollVis();
  const { ref: pRef, offset } = useParallax(.04);

  return (
    <Box ref={(el) => { ref.current = el; pRef.current = el; }} sx={{ py: { xs: 8, md: 12 }, px: 2, background: C.white }}>
      <Container maxWidth="lg">
        <Fade in={vis} timeout={700}>
          <Box sx={{ textAlign: "center", mb: 7, transform: `translateY(${-offset * .15}px)` }}>
            <Chip label="הצצה למערכת" sx={{ mb: 2, background: "#fffbeb", color: C.amber, fontWeight: 700, border: `1px solid ${C.amber}22` }} />
            <Typography variant="h3" sx={{ fontWeight: 900, color: C.textDark, mb: 1.5, fontSize: { xs: "2rem", md: "2.8rem" } }}>
              כך נראית <Box component="span" sx={{ color: C.amber }}>המערכת</Box> מבפנים
            </Typography>
          </Box>
        </Fade>

        <Grid container spacing={4}>
          {screens.map((s, i) => (
            <Grid item xs={12} md={4} key={i}>
              <Zoom in={vis} timeout={400 + i * 180}>
                <Box sx={{ borderRadius: 5, overflow: "hidden", border: `1px solid ${C.border}`, boxShadow: "0 8px 30px rgba(0,0,0,.08)", transition: "all .35s", background: "#fff", "&:hover": { transform: "translateY(-6px) scale(1.02)", boxShadow: "0 16px 50px rgba(0,0,0,.12)" } }}>
                  <Box sx={{ height: 38, background: C.bg, display: "flex", alignItems: "center", px: 1.5, gap: .8, borderBottom: `1px solid ${C.border}` }}>
                    <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: "#ff5f57" }} />
                    <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: "#ffbd2e" }} />
                    <Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: "#28c840" }} />
                    <Box sx={{ flex: 1, mx: 2, height: 22, borderRadius: 12, background: "#fff", border: `1px solid ${C.border}`, display: "flex", alignItems: "center", justifyContent: "center" }}>
                      <Typography sx={{ fontSize: ".65rem", color: C.textLight }}>clickerim.teletop.biz</Typography>
                    </Box>
                  </Box>
                  <Box sx={{ background: s.gradient, minHeight: 220, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", p: 4, color: "#fff", textAlign: "center", position: "relative", overflow: "hidden" }}>
                    <Box sx={{ position: "absolute", width: 120, height: 120, borderRadius: "50%", background: "rgba(255,255,255,.1)", top: -30, right: -30 }} />
                    <Box sx={{ position: "absolute", width: 80, height: 80, borderRadius: "50%", background: "rgba(255,255,255,.08)", bottom: -20, left: -20 }} />
                    <Typography sx={{ fontSize: 48, mb: 1 }}>{s.emoji}</Typography>
                    <Typography variant="h5" sx={{ fontWeight: 800, mb: 1 }}>{s.title}</Typography>
                    <Typography sx={{ opacity: .85, fontSize: ".9rem" }}>{s.desc}</Typography>
                  </Box>
                </Box>
              </Zoom>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};

/* ═══════════════════════════════════════════════════════
   📊 STATS — with count-up animation
   ═══════════════════════════════════════════════════════ */

const statsData = [
  { emoji: "🎮", title: "בזמן אמת", label: "שאלות מתחלפות ותוצאות מיידיות על המסך" },
  { emoji: "📱", title: "טלפון או אפליקציה", label: "מצטרפים מכל מכשיר — גם בשיחת טלפון רגילה" },
  { emoji: "👥", title: "קבוצות ומובילים", label: "טבלת מובילים, דירוג קבוצות ותחרות חיה" },
  { emoji: "⚙️", title: "ניהול פשוט", label: "בונים שאלות ומפעילים משחק מהפאנל בקלות" },
];

const StatItem = ({ data, active, delay }) => (
  <Zoom in={active} timeout={300 + delay}>
    <Box sx={{ textAlign: "center" }}>
      <Typography sx={{ fontSize: "2.4rem", mb: .5 }}>{data.emoji}</Typography>
      <Typography sx={{ fontSize: { xs: "1.25rem", md: "1.55rem" }, fontWeight: 900, color: "#fff", mb: .5 }}>
        {data.title}
      </Typography>
      <Typography sx={{ color: "rgba(255,255,255,.8)", fontWeight: 500, fontSize: ".9rem", lineHeight: 1.6, maxWidth: 220, mx: "auto" }}>{data.label}</Typography>
    </Box>
  </Zoom>
);

const StatsSection = () => {
  const { ref, vis } = useScrollVis(.1);

  return (
    <Box ref={ref} sx={{ py: { xs: 6, md: 10 }, px: 2, background: `linear-gradient(135deg, ${C.primary} 0%, ${C.secondary} 100%)`, position: "relative", overflow: "hidden" }}>
      {/* decorative circles */}
      <Box sx={{ position: "absolute", width: 200, height: 200, borderRadius: "50%", background: "rgba(255,255,255,.06)", top: -60, right: -40 }} />
      <Box sx={{ position: "absolute", width: 150, height: 150, borderRadius: "50%", background: "rgba(255,255,255,.04)", bottom: -30, left: -30 }} />
      <Container maxWidth="md">
        <Grid container spacing={4}>
          {statsData.map((s, i) => (
            <Grid item xs={6} md={3} key={i}>
              <StatItem data={s} active={vis} delay={i * 150} />
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};

/* ═══════════════════════════════════════════════════════
   🏢 USE CASES — who uses this
   ═══════════════════════════════════════════════════════ */

const useCases = [
  { icon: <SchoolIcon sx={{ fontSize: 34 }} />, title: "מורים ומחנכים", desc: "חידונים בכיתה, חזרה למבחנים ושיעורים חוויתיים", color: C.primary, bg: "#eef2ff", emoji: "📚" },
  { icon: <CelebrationIcon sx={{ fontSize: 34 }} />, title: "מנחי אירועים", desc: "תחנות חידון בחתונות, בר מצוות וימי הולדת", color: C.pink, bg: "#fdf2f8", emoji: "🎉" },
  { icon: <BusinessIcon sx={{ fontSize: 34 }} />, title: "חברות וארגונים", desc: "גיבוש צוות, הכשרות ובדיקות ידע לעובדים", color: C.emerald, bg: "#ecfdf5", emoji: "💼" },
  { icon: <FamilyRestroomIcon sx={{ fontSize: 34 }} />, title: "משפחה וחברים", desc: "ערבי משפחה, שבתות, חגים וחופשות כייפיות", color: C.amber, bg: "#fffbeb", emoji: "👨‍👩‍👧‍👦" },
];

const UseCasesSection = () => {
  const { ref, vis } = useScrollVis();

  return (
    <Box id="usecases" ref={ref} sx={{ py: { xs: 8, md: 12 }, px: 2, background: C.bg }}>
      <Container maxWidth="lg">
        <Fade in={vis} timeout={700}>
          <Box sx={{ textAlign: "center", mb: 7 }}>
            <Chip label="למי זה מתאים" sx={{ mb: 2, background: "#ecfeff", color: C.cyan, fontWeight: 700, border: `1px solid ${C.cyan}22` }} />
            <Typography variant="h3" sx={{ fontWeight: 900, color: C.textDark, mb: 1.5, fontSize: { xs: "2rem", md: "2.8rem" } }}>
              קליקרים מתאים ל<Box component="span" sx={{ color: C.cyan }}>כולם</Box>
            </Typography>
            <Typography sx={{ color: C.textMid, fontSize: "1.1rem", maxWidth: 520, mx: "auto" }}>
              מורים, מנחים, חברות ומשפחות — כל אחד ימצא שימוש מושלם
            </Typography>
          </Box>
        </Fade>

        <Grid container spacing={3}>
          {useCases.map((uc, i) => (
            <Grid item xs={12} sm={6} md={3} key={i}>
              <Slide in={vis} direction="up" timeout={400 + i * 150}>
                <ModernCard accent={uc.color} sx={{ height: "100%" }}>
                  <CardContent sx={{ p: 3.5, textAlign: "center" }}>
                    <Box sx={{ position: "absolute", top: -12, right: 16, fontSize: 20, width: 32, height: 32, borderRadius: "9px", background: uc.bg, border: `1px solid ${uc.color}22`, display: "flex", alignItems: "center", justifyContent: "center" }}>{uc.emoji}</Box>
                    <Box sx={{ width: 68, height: 68, borderRadius: "22px", mx: "auto", mb: 2, background: uc.bg, display: "flex", alignItems: "center", justifyContent: "center", color: uc.color, transition: "all .3s", "&:hover": { transform: "scale(1.1) rotate(5deg)" } }}>{uc.icon}</Box>
                    <Typography variant="h6" sx={{ fontWeight: 800, color: C.textDark, mb: 1, fontSize: "1.02rem" }}>{uc.title}</Typography>
                    <Typography sx={{ color: C.textMid, lineHeight: 1.8, fontSize: ".88rem" }}>{uc.desc}</Typography>
                  </CardContent>
                </ModernCard>
              </Slide>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};

/* ═══════════════════════════════════════════════════════
   ⚖️ COMPARISON — us vs others
   ═══════════════════════════════════════════════════════ */

const comparisonRows = [
  { feature: "ממשק בעברית מלאה (RTL)", us: true, them: false },
  { feature: "משחק דרך שיחת טלפון", us: true, them: false },
  { feature: "ללא הורדה, אפליקציה או אינטרנט", us: true, them: false },
  { feature: "הרשמה ויצירת משחקים בחינם", us: true, them: false },
  { feature: "תמיכה בקבוצות ותחרויות", us: true, them: "חלקי" },
  { feature: "העלאת מדיה (מוזיקה, תמונות)", us: true, them: "חלקי" },
  { feature: "מסך ניצחון עם אנימציות", us: true, them: false },
  { feature: "לוח מובילים בזמן אמת", us: true, them: true },
  { feature: "פאנל ניהול מתקדם", us: true, them: "חלקי" },
];

const ComparisonSection = () => {
  const { ref, vis } = useScrollVis();

  const renderCheck = (val) => {
    if (val === true) return <DoneIcon sx={{ color: C.emerald, fontSize: 22 }} />;
    if (val === false) return <CloseIcon sx={{ color: C.rose, fontSize: 22 }} />;
    return <Typography sx={{ color: C.amber, fontWeight: 700, fontSize: ".8rem" }}>{val}</Typography>;
  };

  return (
    <Box ref={ref} sx={{ py: { xs: 8, md: 12 }, px: 2, background: C.white }}>
      <Container maxWidth="md">
        <Fade in={vis} timeout={700}>
          <Box sx={{ textAlign: "center", mb: 7 }}>
            <Chip label="השוואה" sx={{ mb: 2, background: "#f5f3ff", color: C.secondary, fontWeight: 700, border: `1px solid ${C.secondary}22` }} />
            <Typography variant="h3" sx={{ fontWeight: 900, color: C.textDark, mb: 1.5, fontSize: { xs: "2rem", md: "2.8rem" } }}>
              למה <Box component="span" sx={{ color: C.primary }}>קליקרים</Box> ולא משהו אחר?
            </Typography>
          </Box>
        </Fade>

        <Zoom in={vis} timeout={600}>
          <Box sx={{ borderRadius: 5, overflow: "hidden", border: `1px solid ${C.border}`, boxShadow: "0 4px 24px rgba(0,0,0,.06)" }}>
            {/* header */}
            <Box sx={{ display: "flex", background: `linear-gradient(135deg, ${C.primary}, ${C.secondary})`, color: "#fff" }}>
              <Box sx={{ flex: 2, p: 2, fontWeight: 700, fontSize: ".95rem", display: "flex", alignItems: "center" }}>
                <CompareIcon sx={{ ml: 1, fontSize: 20 }} /> תכונה
              </Box>
              <Box sx={{ flex: 1, p: 2, textAlign: "center", fontWeight: 800, fontSize: ".95rem", borderRight: "1px solid rgba(255,255,255,.2)" }}>
                ⚡ קליקרים
              </Box>
              <Box sx={{ flex: 1, p: 2, textAlign: "center", fontWeight: 600, fontSize: ".85rem", borderRight: "1px solid rgba(255,255,255,.2)", opacity: .8 }}>
                אחרים
              </Box>
            </Box>
            {/* rows */}
            {comparisonRows.map((row, i) => (
              <Box key={i} sx={{
                display: "flex", alignItems: "center",
                background: i % 2 === 0 ? "#fff" : C.bg,
                borderTop: `1px solid ${C.border}`,
                transition: "background .2s",
                "&:hover": { background: C.primaryXL },
              }}>
                <Box sx={{ flex: 2, p: 2, fontWeight: 500, fontSize: ".9rem", color: C.textDark }}>{row.feature}</Box>
                <Box sx={{ flex: 1, p: 2, display: "flex", justifyContent: "center", borderRight: `1px solid ${C.border}` }}>{renderCheck(row.us)}</Box>
                <Box sx={{ flex: 1, p: 2, display: "flex", justifyContent: "center", borderRight: `1px solid ${C.border}` }}>{renderCheck(row.them)}</Box>
              </Box>
            ))}
          </Box>
        </Zoom>
      </Container>
    </Box>
  );
};

/* ═══════════════════════════════════════════════════════
   💬 TESTIMONIALS
   ═══════════════════════════════════════════════════════ */

const benefits = [
  { title: "קל להתחיל", text: "יוצרים משחק, מוסיפים שאלות ומפעילים — בלי התקנות ובלי כאב ראש.", emoji: "⚡", color: C.primary, bg: "#eef2ff" },
  { title: "מתאים לכל קהל", text: "כיתות, אירועים, גיבוש צוות ומשפחה — כולם מצטרפים ומשתתפים בקלות.", emoji: "🎯", color: C.emerald, bg: "#ecfdf5" },
  { title: "חוויה סוחפת", text: "שאלות בזמן אמת, טבלת מובילים ותחרות חיה שמלהיבה את כל המשתתפים.", emoji: "🚀", color: C.pink, bg: "#fdf2f8" },
];

const TestimonialsSection = () => {
  const { ref, vis } = useScrollVis();

  return (
    <Box ref={ref} sx={{ py: { xs: 8, md: 12 }, px: 2, background: C.bg }}>
      <Container maxWidth="lg">
        <Fade in={vis} timeout={700}>
          <Box sx={{ textAlign: "center", mb: 7 }}>
            <Chip label="למה קליקרים" sx={{ mb: 2, background: "#fdf2f8", color: C.pink, fontWeight: 700, border: `1px solid ${C.pink}22` }} />
            <Typography variant="h3" sx={{ fontWeight: 900, color: C.textDark, fontSize: { xs: "2rem", md: "2.8rem" } }}>
              כל מה שצריך ל<Box component="span" sx={{ color: C.pink }}>משחק מנצח</Box>
            </Typography>
          </Box>
        </Fade>

        <Grid container spacing={4}>
          {benefits.map((b, i) => (
            <Grid item xs={12} md={4} key={i}>
              <Slide in={vis} direction="up" timeout={400 + i * 180}>
                <ModernCard accent={b.color} sx={{ height: "100%" }}>
                  <CardContent sx={{ p: 3.5 }}>
                    <Box sx={{ width: 56, height: 56, borderRadius: "16px", background: b.bg, border: `1px solid ${b.color}22`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 28, mb: 2.5 }}>{b.emoji}</Box>
                    <Typography sx={{ color: C.textDark, fontWeight: 800, fontSize: "1.25rem", mb: 1 }}>{b.title}</Typography>
                    <Typography sx={{ color: C.textMid, fontSize: "1rem", lineHeight: 1.9 }}>{b.text}</Typography>
                  </CardContent>
                </ModernCard>
              </Slide>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};

/* ═══════════════════════════════════════════════════════
   📞 CONTACT — צור קשר
   ═══════════════════════════════════════════════════════ */

const ContactSection = () => {
  const { ref, vis } = useScrollVis();
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [sending, setSending] = useState(false);
  const [snack, setSnack] = useState({ open: false, msg: "", sev: "success" });

  const handleChange = (e) => setForm((p) => ({ ...p, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      setSnack({ open: true, msg: "נא למלא את כל השדות", sev: "warning" });
      return;
    }
    setSending(true);
    try {
      const res = await fetch(`${API_URL}/crm/tickets`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: form.name, email: form.email, message: form.message, subject: "פנייה מהאתר" }),
      });
      const data = await res.json();
      if (data.status === "success") {
        setSnack({ open: true, msg: "ההודעה נשלחה בהצלחה! נחזור אליכם בהקדם 🎉", sev: "success" });
        setForm({ name: "", email: "", message: "" });
      } else {
        setSnack({ open: true, msg: "שגיאה בשליחה, נסו שוב", sev: "error" });
      }
    } catch {
      setSnack({ open: true, msg: "שגיאה בשליחה, נסו שוב", sev: "error" });
    }
    setSending(false);
  };

  const contactInfo = [
    { icon: <EmailIcon sx={{ fontSize: 24 }} />, label: "אימייל", value: "cs@clickerim.co.il", color: C.primary, bg: "#eef2ff" },
    { icon: <WhatsAppIcon sx={{ fontSize: 24 }} />, label: "וואטסאפ", value: "054-9999205", color: C.emerald, bg: "#ecfdf5" },
    { icon: <AccessTimeIcon sx={{ fontSize: 24 }} />, label: "זמני מענה", value: "א-ה 9:00-18:00", color: C.amber, bg: "#fffbeb" },
  ];

  return (
    <Box id="contact" ref={ref} sx={{
      py: { xs: 8, md: 12 }, px: 2,
      background: `linear-gradient(170deg, #f0f4ff 0%, #faf5ff 40%, ${C.bg} 100%)`,
      position: "relative", overflow: "hidden",
    }}>
      {/* decorative blobs */}
      <Box sx={{ position: "absolute", width: 300, height: 300, borderRadius: "50%", background: `radial-gradient(circle, ${C.primary}12, transparent)`, top: -80, left: -80, filter: "blur(50px)" }} />
      <Box sx={{ position: "absolute", width: 250, height: 250, borderRadius: "50%", background: `radial-gradient(circle, ${C.pink}10, transparent)`, bottom: -60, right: -60, filter: "blur(50px)" }} />

      <Container maxWidth="lg">
        <Fade in={vis} timeout={700}>
          <Box sx={{ textAlign: "center", mb: 7 }}>
            <Chip label="צור קשר" sx={{ mb: 2, background: C.primaryXL, color: C.primary, fontWeight: 700, border: `1px solid ${C.primary}22` }} />
            <Typography variant="h3" sx={{ fontWeight: 900, color: C.textDark, mb: 1.5, fontSize: { xs: "2rem", md: "2.8rem" } }}>
              יש לכם <Box component="span" sx={{ color: C.primary }}>שאלה</Box>? דברו איתנו!
            </Typography>
            <Typography sx={{ color: C.textMid, fontSize: "1.1rem", maxWidth: 480, mx: "auto" }}>
              נשמח לעזור, לענות על שאלות ולשמוע הצעות לשיפור
            </Typography>
          </Box>
        </Fade>

        <Grid container spacing={5}>
          {/* contact info cards */}
          <Grid item xs={12} md={5}>
            <Slide in={vis} direction="left" timeout={600}>
              <Box>
                {contactInfo.map((c, i) => (
                  <Box key={i} sx={{
                    display: "flex", alignItems: "center", gap: 2.5, p: 3, mb: 2.5,
                    borderRadius: 4, background: "#fff", border: `1px solid ${C.border}`,
                    boxShadow: "0 2px 12px rgba(0,0,0,.04)",
                    transition: "all .3s",
                    "&:hover": { transform: "translateX(-6px)", boxShadow: `0 6px 24px ${c.color}15`, borderColor: c.color + "44" },
                  }}>
                    <Box sx={{ width: 52, height: 52, borderRadius: "16px", background: c.bg, display: "flex", alignItems: "center", justifyContent: "center", color: c.color, flexShrink: 0 }}>{c.icon}</Box>
                    <Box>
                      <Typography sx={{ color: C.textLight, fontSize: ".8rem", fontWeight: 600, mb: .3 }}>{c.label}</Typography>
                      <Typography sx={{ color: C.textDark, fontWeight: 700, fontSize: "1rem" }}>{c.value}</Typography>
                    </Box>
                  </Box>
                ))}

                {/* support badge */}
                <Box sx={{
                  mt: 3, p: 3, borderRadius: 4,
                  background: `linear-gradient(135deg, ${C.primary}08, ${C.secondary}08)`,
                  border: `1px solid ${C.primary}15`,
                  display: "flex", alignItems: "center", gap: 2,
                }}>
                  <SupportAgentIcon sx={{ fontSize: 36, color: C.primary }} />
                  <Box>
                    <Typography sx={{ fontWeight: 700, color: C.textDark, fontSize: ".95rem" }}>תמיכה מהירה</Typography>
                    <Typography sx={{ color: C.textMid, fontSize: ".85rem" }}>זמן מענה ממוצע: עד 2 שעות</Typography>
                  </Box>
                </Box>
              </Box>
            </Slide>
          </Grid>

          {/* contact form */}
          <Grid item xs={12} md={7}>
            <Slide in={vis} direction="right" timeout={600}>
              <Box component="form" onSubmit={handleSubmit} sx={{
                p: { xs: 3, md: 4.5 }, borderRadius: 5, background: "#fff",
                border: `1px solid ${C.border}`, boxShadow: "0 8px 40px rgba(0,0,0,.06)",
              }}>
                <Typography variant="h5" sx={{ fontWeight: 800, color: C.textDark, mb: 3 }}>
                  <SendIcon sx={{ ml: 1, fontSize: 22, color: C.primary }} /> שלחו לנו הודעה
                </Typography>

                <Grid container spacing={2.5}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth name="name" label="שם מלא" value={form.name} onChange={handleChange}
                      variant="outlined"
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 3,
                          "&:hover fieldset": { borderColor: C.primaryL },
                          "&.Mui-focused fieldset": { borderColor: C.primary },
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth name="email" label="אימייל" type="email" value={form.email} onChange={handleChange}
                      variant="outlined"
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 3,
                          "&:hover fieldset": { borderColor: C.primaryL },
                          "&.Mui-focused fieldset": { borderColor: C.primary },
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth name="message" label="ההודעה שלכם" value={form.message} onChange={handleChange}
                      multiline rows={4} variant="outlined"
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: 3,
                          "&:hover fieldset": { borderColor: C.primaryL },
                          "&.Mui-focused fieldset": { borderColor: C.primary },
                        },
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Button type="submit" disabled={sending} fullWidth sx={{
                      background: `linear-gradient(135deg, ${C.primary}, ${C.secondary})`,
                      color: "#fff", fontWeight: 800, fontSize: "1.05rem",
                      borderRadius: 50, py: 1.8, textTransform: "none",
                      boxShadow: `0 6px 20px ${C.primary}33`,
                      "&:hover": { transform: "scale(1.02)", boxShadow: `0 8px 28px ${C.primary}44` },
                      "&:disabled": { opacity: .6 },
                      transition: "all .3s",
                    }}>
                      {sending ? "שולח..." : "📨 שליחת הודעה"}
                    </Button>
                  </Grid>
                </Grid>
              </Box>
            </Slide>
          </Grid>
        </Grid>
      </Container>

      <Snackbar open={snack.open} autoHideDuration={4000} onClose={() => setSnack((p) => ({ ...p, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}>
        <Alert severity={snack.sev} variant="filled" sx={{ borderRadius: 3, fontWeight: 600 }}>{snack.msg}</Alert>
      </Snackbar>
    </Box>
  );
};

/* ═══════════════════════════════════════════════════════
   🎯 CTA
   ═══════════════════════════════════════════════════════ */

const CTASection = () => {
  const nav = useNavigate();
  const { ref, vis } = useScrollVis(.1);
  const { ref: pRef, offset } = useParallax(.08);

  return (
    <Box ref={(el) => { ref.current = el; pRef.current = el; }} sx={{
      py: { xs: 8, md: 14 }, px: 2, position: "relative", overflow: "hidden",
      background: `linear-gradient(170deg, #f0f4ff 0%, #faf5ff 40%, #fef3c7 100%)`,
    }}>
      <Box sx={{ position: "absolute", width: 350, height: 350, borderRadius: "50%", background: `radial-gradient(circle, ${C.primary}18, transparent)`, top: -80, right: -80, filter: "blur(50px)", transform: `translateY(${offset * .3}px)` }} />
      <Box sx={{ position: "absolute", width: 280, height: 280, borderRadius: "50%", background: `radial-gradient(circle, ${C.pink}14, transparent)`, bottom: -60, left: -60, filter: "blur(50px)", transform: `translateY(${-offset * .3}px)` }} />
      <Box sx={{ position: "absolute", width: 200, height: 200, borderRadius: "50%", background: `radial-gradient(circle, ${C.amber}12, transparent)`, top: "40%", left: "60%", filter: "blur(40px)" }} />

      <Fade in={vis} timeout={700}>
        <Box sx={{ textAlign: "center", position: "relative", zIndex: 2, transform: `translateY(${-offset * .1}px)` }}>
          <Typography sx={{ fontSize: 56, mb: 2 }}>🚀</Typography>
          <Typography variant="h3" sx={{ fontWeight: 900, color: C.textDark, mb: 2, fontSize: { xs: "1.8rem", md: "2.8rem" } }}>
            מוכנים ליצור את <Box component="span" sx={{ color: C.primary }}>המשחק הבא</Box> שלכם?
          </Typography>
          <Typography sx={{ color: C.textMid, fontSize: "1.1rem", mb: 5, maxWidth: 480, mx: "auto" }}>
            הצטרפו לאלפי מנחים, מורים ואנשי מקצוע שכבר משתמשים בקליקרים
          </Typography>
          <Box sx={{ display: "flex", gap: 2, justifyContent: "center", flexWrap: "wrap" }}>
            <Button onClick={() => nav("/login?tab=register")} sx={{
              background: `linear-gradient(135deg, ${C.primary}, ${C.secondary})`,
              backgroundSize: "200% 200%",
              animation: `${gradientShift} 4s ease infinite, ${pulseGlow} 3s infinite`,
              color: "#fff", fontWeight: 800, fontSize: "1.2rem",
              borderRadius: 50, px: 6, py: 2, textTransform: "none",
              boxShadow: `0 8px 28px ${C.primary}44`,
              "&:hover": { transform: "scale(1.04)", boxShadow: `0 12px 36px ${C.primary}55` },
              transition: "all .3s",
            }}>
              <PlayArrowIcon sx={{ ml: 1 }} /> {CTA_LABEL}
            </Button>
            <Button onClick={() => document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" })} sx={{
              color: C.primary, fontWeight: 700, fontSize: "1rem",
              borderRadius: 50, px: 4, py: 1.5, border: `2px solid ${C.primary}44`,
              textTransform: "none", background: "#fff",
              "&:hover": { background: C.primaryXL, borderColor: C.primary },
            }}>
              <EmailIcon sx={{ ml: 1, fontSize: 20 }} /> צרו קשר
            </Button>
          </Box>
          <Box sx={{ mt: 4, display: "flex", gap: 3, justifyContent: "center", flexWrap: "wrap" }}>
            {[
              { icon: <CheckCircleIcon sx={{ fontSize: 16, color: C.emerald }} />, text: "ללא כרטיס אשראי" },
              { icon: <BoltIcon sx={{ fontSize: 16, color: C.amber }} />, text: "התחלה מיידית" },
              { icon: <FavoriteIcon sx={{ fontSize: 16, color: C.pink }} />, text: "תמיכה מלאה" },
            ].map((b, i) => (
              <Box key={i} sx={{ display: "flex", alignItems: "center", gap: .7, color: C.textMid, fontSize: ".85rem" }}>{b.icon} <span>{b.text}</span></Box>
            ))}
          </Box>
        </Box>
      </Fade>
    </Box>
  );
};

/* ═══════════════════════════════════════════════════════
   📌 FOOTER — extended
   ═══════════════════════════════════════════════════════ */

const Footer = () => {
  const nav = useNavigate();
  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

  return (
    <Box component="footer" sx={{ py: 6, px: 3, background: C.textDark, color: "#fff" }}>
      <Container maxWidth="lg">
        <Grid container spacing={4} sx={{ mb: 4 }}>
          {/* brand */}
          <Grid item xs={12} md={4}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
              <Box sx={{ width: 38, height: 38, borderRadius: "12px", background: `linear-gradient(135deg, ${C.primary}, ${C.secondary})`, display: "flex", alignItems: "center", justifyContent: "center" }}>
                <SportsEsportsIcon sx={{ color: "#fff", fontSize: 22 }} />
              </Box>
              <Typography sx={{ fontWeight: 900, fontSize: "1.3rem", color: "#fff" }}>קליקרים</Typography>
            </Box>
            <Typography sx={{ color: "rgba(255,255,255,.5)", fontSize: ".9rem", lineHeight: 1.8, maxWidth: 300 }}>
              הפלטפורמה המובילה בישראל לחידונים טלפוניים — השחקנים מתקשרים, לוחצים ומנצחים!
            </Typography>
          </Grid>

          {/* links */}
          <Grid item xs={6} md={2}>
            <Typography sx={{ fontWeight: 700, color: "#fff", mb: 2, fontSize: ".95rem" }}>ניווט</Typography>
            {[{ label: "דף הבית", action: () => { nav("/"); window.scrollTo({ top: 0 }); } },
              { label: "אודות", action: () => nav("/about") },
              { label: "כניסה", action: () => nav("/login") },
            ].map((l, i) => (
              <Typography key={i} onClick={l.action} sx={{ color: "rgba(255,255,255,.5)", cursor: "pointer", fontSize: ".88rem", mb: 1.2, "&:hover": { color: "#fff" }, transition: "color .2s" }}>{l.label}</Typography>
            ))}
          </Grid>

          <Grid item xs={6} md={2}>
            <Typography sx={{ fontWeight: 700, color: "#fff", mb: 2, fontSize: ".95rem" }}>סעיפים</Typography>
            {navLinks.map((l, i) => (
              <Typography key={i} onClick={() => scrollTo(l.id)} sx={{ color: "rgba(255,255,255,.5)", cursor: "pointer", fontSize: ".88rem", mb: 1.2, "&:hover": { color: "#fff" }, transition: "color .2s" }}>{l.label}</Typography>
            ))}
          </Grid>

          {/* contact */}
          <Grid item xs={12} md={4}>
            <Typography sx={{ fontWeight: 700, color: "#fff", mb: 2, fontSize: ".95rem" }}>צרו קשר</Typography>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1.5 }}>
              <EmailIcon sx={{ fontSize: 18, color: "rgba(255,255,255,.4)" }} />
              <Typography sx={{ color: "rgba(255,255,255,.6)", fontSize: ".88rem" }}>cs@clickerim.co.il</Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1.5 }}>
              <WhatsAppIcon sx={{ fontSize: 18, color: "rgba(255,255,255,.4)" }} />
              <Typography sx={{ color: "rgba(255,255,255,.6)", fontSize: ".88rem" }}>054-9999205</Typography>
            </Box>
            <Box sx={{ display: "flex", gap: 1.5, mt: 2 }}>
              {[
                { icon: <WhatsAppIcon sx={{ fontSize: 20 }} />, color: "#25D366" },
                { icon: <EmailIcon sx={{ fontSize: 20 }} />, color: C.primary },
              ].map((s, i) => (
                <Box key={i} sx={{
                  width: 38, height: 38, borderRadius: "10px", background: "rgba(255,255,255,.08)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  color: "rgba(255,255,255,.6)", cursor: "pointer",
                  transition: "all .2s",
                  "&:hover": { background: s.color, color: "#fff", transform: "scale(1.1)" },
                }}>{s.icon}</Box>
              ))}
            </Box>
          </Grid>
        </Grid>

        {/* bottom bar */}
        <Box sx={{ borderTop: "1px solid rgba(255,255,255,.1)", pt: 3, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 2 }}>
          <Typography sx={{ color: "rgba(255,255,255,.3)", fontSize: ".82rem" }}>
            © {new Date().getFullYear()} קליקרים — כל הזכויות שמורות
          </Typography>
          <Typography sx={{ color: "rgba(255,255,255,.2)", fontSize: ".75rem" }}>
            נבנה עם ❤️ לחוויות משחק מדהימות
          </Typography>
        </Box>
      </Container>
    </Box>
  );
};

/* ═══════════════════════════════════════════════════════
   ⬆️ BACK TO TOP — with progress indicator
   ═══════════════════════════════════════════════════════ */

const BackToTop = () => {
  const [show, setShow] = useState(false);
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const h = () => {
      setShow(window.scrollY > 500);
      const total = document.documentElement.scrollHeight - window.innerHeight;
      setProgress(total > 0 ? (window.scrollY / total) * 100 : 0);
    };
    window.addEventListener("scroll", h, { passive: true });
    return () => window.removeEventListener("scroll", h);
  }, []);

  if (!show) return null;

  return (
    <Zoom in>
      <Box sx={{ position: "fixed", bottom: 24, left: 24, zIndex: 100 }}>
        {/* circular progress ring */}
        <Box sx={{ position: "relative", width: 54, height: 54 }}>
          <svg width="54" height="54" style={{ position: "absolute", top: 0, left: 0, transform: "rotate(-90deg)" }}>
            <circle cx="27" cy="27" r="24" fill="none" stroke="rgba(255,255,255,.2)" strokeWidth="3" />
            <circle cx="27" cy="27" r="24" fill="none" stroke="#fff" strokeWidth="3"
              strokeDasharray={`${2 * Math.PI * 24}`}
              strokeDashoffset={`${2 * Math.PI * 24 * (1 - progress / 100)}`}
              strokeLinecap="round"
              style={{ transition: "stroke-dashoffset .1s" }}
            />
          </svg>
          <IconButton onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} sx={{
            position: "absolute", top: 2, left: 2, width: 50, height: 50,
            background: `linear-gradient(135deg, ${C.primary}, ${C.secondary})`, color: "#fff",
            boxShadow: `0 4px 18px ${C.primary}44`,
            "&:hover": { transform: "scale(1.1)" }, transition: "all .3s",
          }}>
            <KeyboardArrowUpIcon />
          </IconButton>
        </Box>
      </Box>
    </Zoom>
  );
};

/* ═══════════════════════════════════════════════════════
   🏠 MAIN LANDING PAGE
   ═══════════════════════════════════════════════════════ */

const LandingPage = () => (
  <Box sx={{ width: "100%", minHeight: "100vh" }}>
    <Navbar />
    <HeroSection />
    <FeaturesSection />
    <HowItWorksSection />
    <DemoSection />
    <StatsSection />
    <UseCasesSection />
    <ComparisonSection />
    <TestimonialsSection />
    <ContactSection />
    <CTASection />
    <Footer />
    <BackToTop />
  </Box>
);

export default LandingPage;
